import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link2, Copy, Trash2, Loader2, ExternalLink, MousePointerClick, TrendingUp, Smartphone, Monitor, Tablet } from "lucide-react";

function MiniBarChart({ data }) {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-12">
      {data.map(d => (
        <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-0.5" title={`${d.date}: ${d.count} click`}>
          <div
            className="w-full bg-lime-400/70 rounded-sm min-h-[2px]"
            style={{ height: `${(d.count / max) * 100}%` }}
          />
          <span className="text-[9px] text-slate-500">{d.date.slice(8, 10)}</span>
        </div>
      ))}
    </div>
  );
}

const DEVICE_ICON = { mobile: Smartphone, desktop: Monitor, tablet: Tablet };

export default function ShortLinkManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ label: "", target_url: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/short-links");
      setItems(data.short_links || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Không tải được danh sách link");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const target_url = form.target_url.trim();
    if (!target_url) { toast.error("Vui lòng nhập link đích"); return; }
    setCreating(true);
    try {
      await api.post("/short-links", { label: form.label.trim(), target_url });
      toast.success("Đã tạo link rút gọn");
      setForm({ label: "", target_url: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi tạo link");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Xoá link "${label || "chưa đặt tên"}"?`)) return;
    try {
      await api.delete(`/short-links/${id}`);
      toast.success("Đã xoá link");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xoá");
    }
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Đã copy link");
    } catch { toast.error("Không copy được"); }
  };

  return (
    <div className="space-y-6" data-testid="short-link-manager">
      {/* Create form */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-4">
          <Link2 className="w-5 h-5 text-sky-400" />
          <h3 className="text-base font-heading font-semibold text-[#F8FAFC]">Tạo link rút gọn</h3>
        </div>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tên gợi nhớ</label>
            <Input
              value={form.label}
              onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="VD: Menu Zalo / Facebook..."
              className="bg-[#0f172a] border-[#334155] text-slate-200"
              data-testid="short-link-label-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Link đích *</label>
            <Input
              value={form.target_url}
              onChange={(e) => setForm(f => ({ ...f, target_url: e.target.value }))}
              placeholder="https://facebook.com/karinox-page..."
              className="bg-[#0f172a] border-[#334155] text-slate-200"
              data-testid="short-link-url-input"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={creating}
            className="bg-sky-500 hover:bg-sky-600 text-white font-semibold h-10 whitespace-nowrap"
            data-testid="short-link-create-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rút gọn"}
          </Button>
        </form>
        <p className="text-[11px] text-slate-500 mt-2">
          Link rút gọn tự tạo slug 6 ký tự ngẫu nhiên, đếm chi tiết lượt click theo 7 ngày + thiết bị + nguồn.
        </p>
      </div>

      {/* List */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-heading font-semibold text-[#F8FAFC]">Danh sách link</h3>
          <Badge className="bg-slate-700/60 text-slate-300 border-slate-600 border">
            {items.length} link
          </Badge>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-sky-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">
            <Link2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
            Chưa có link rút gọn nào.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(item => (
              <div
                key={item.id}
                className="bg-[#0f172a] border border-[#334155] rounded-lg p-4"
                data-testid={`short-link-item-${item.id}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
                  {/* Left: info */}
                  <div className="space-y-2 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-100">
                        {item.label || <span className="italic text-slate-500">(chưa đặt tên)</span>}
                      </span>
                      <Badge className="bg-lime-500/15 text-lime-400 border-lime-500/30 border text-[10px] px-1.5">
                        <MousePointerClick className="w-3 h-3 mr-1" />
                        {item.total_clicks} click
                      </Badge>
                      {item.stats?.clicks_7d > 0 && (
                        <Badge className="bg-sky-500/15 text-sky-400 border-sky-500/30 border text-[10px] px-1.5">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          {item.stats.clicks_7d} tuần này
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      <span className="text-slate-500 shrink-0">Ngắn:</span>
                      <code className="bg-[#1e293b] px-2 py-0.5 rounded text-sky-400 truncate">
                        {item.short_url}
                      </code>
                      <button
                        onClick={() => handleCopy(item.short_url)}
                        className="text-slate-500 hover:text-sky-400 shrink-0"
                        data-testid={`short-link-copy-short-${item.id}`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      <span className="text-slate-500 shrink-0">Đích:</span>
                      <a
                        href={item.target_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-slate-300 hover:text-lime-400 truncate hover:underline"
                        title={item.target_url}
                      >
                        {item.target_url}
                      </a>
                      <ExternalLink className="w-3 h-3 text-slate-600 shrink-0" />
                    </div>
                    {/* Mini chart 7 ngày */}
                    {item.stats?.last_7_days && (
                      <div className="pt-2">
                        <div className="text-[10px] text-slate-500 mb-1">Click 7 ngày qua</div>
                        <MiniBarChart data={item.stats.last_7_days} />
                      </div>
                    )}
                    {/* Device + Referer breakdown */}
                    {(item.stats?.device_breakdown?.length > 0 || item.stats?.top_referers?.length > 0) && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 text-[11px]">
                        {item.stats?.device_breakdown?.map(d => {
                          const Icon = DEVICE_ICON[d.device] || Monitor;
                          return (
                            <span key={d.device} className="flex items-center gap-1 text-slate-400">
                              <Icon className="w-3 h-3" />
                              {d.device}: <b className="text-slate-200">{d.count}</b>
                            </span>
                          );
                        })}
                        {item.stats?.top_referers?.slice(0, 3).map(r => (
                          <span key={r.referer} className="text-slate-400">
                            {r.referer}: <b className="text-slate-200">{r.count}</b>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Right: actions */}
                  <div className="flex lg:flex-col gap-1.5 lg:items-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(item.id, item.label)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-xs"
                      data-testid={`short-link-delete-${item.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
